<template>
    <div class="card" v-if="registrations">

        <div class="card-header">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.degrees') }}</h3>
                </div>
                <div class="col-6 text-right">
                    <button-radio-group 
                        :options="orderOptions"
                        button-classes="btn-secondary btn-sm"
                        v-model="order"
                        class="d-inline">
                    </button-radio-group>
                </div>
            </div>
        </div>

        <p class="card-body mt-2" v-if="empty">
            <strong>{{ $t('dashboard.no-degree') }}</strong>
        </p>

        <div v-if="!empty">
            
            <el-table class="table-responsive table-flush"
                    :show-header="false"
                    :data="visibleRegistrations">

                <el-table-column>
                    <template v-slot="{row}">

                        <div class="padding" :class="{ disabled: row.withdrawed }">
                            <h3 class="mb-2">
                                <a href="#" @click.prevent="openRegistration(row)" v-if="canReadRegistration(row)">
                                    {{ row.degree.name }}
                                </a>
                                <span v-else>{{ row.degree.name }}</span>
                            </h3>

                            <base-progress type="info" :value="progress(row)" v-if="!row.withdrawed">
                            </base-progress>

                            <span class="d-block" v-if="!row.withdrawed">
                                <strong>{{ progress(row) }}%</strong> {{ $t('dashboard.done').toLowerCase() }}
                                <span v-if="row.state.remedial_sessions">
                                    + <strong>{{ row.state.remedial_sessions }}</strong> {{ $t('dashboard.remedial-s').toLowerCase() }}
                                </span>
                            </span>
                        </div>

                    </template>
                </el-table-column>

                <el-table-column>
                    <template v-slot="{row}">

                        <div class="padding" :class="{ disabled: row.withdrawed }">

                            <span class="d-block mb-1" v-if="row.state.from">
                                <i class="fas fa-calendar-alt mr-1"></i> {{ date(row.state.from) }} to {{ date(row.state.to) }}
                            </span>

                            <span class="d-block mb-1">
                                <a href="#" @click.prevent="openCohort(row.cohort)" class="black-link"
                                    v-if="$permissions.hasRight('dashboard.learners.read') && row.explorable"
                                >
                                    <i class="fas fa-users mr-1"></i> {{ row.cohort.name }}
                                </a>
                                <span v-else>
                                    <i class="fas fa-users mr-1"></i> {{ row.cohort.name }}
                                </span>
                            </span>

                            <span class="d-block mb-1" v-if="row.state.meta && row.state.meta.go_ground">
                                <i class="fas fa-plane-departure mr-1"></i>
                                {{ $t('degrees.go-ground') }}
                            </span>
                        </div>

                    </template>
                </el-table-column>

                <el-table-column>
                    <template v-slot="{row}">
                        
                        <badge type="secondary" v-if="row.status == 'archived'">
                            {{ $t('status.archived') }}
                        </badge>

                        <!--
                        <span class="d-block mb-1" v-if="row.state.meta && row.state.meta.closing && row.state.meta.closing.date">
                            
                            <span v-if="registrationClosed(row)">
                                <badge type="success" class="mb-1">
                                    <i class="fas fa-lock mr-1"></i> {{ row.state.meta.closing.date }}
                                </badge>
                            </span>
                            
                            <small v-else>
                                <i class="fas fa-lock mr-1"></i> {{ row.state.meta.closing.date }}
                            </small>
                        </span>

                        <small class="d-block mb-1" v-if="row.state.meta && row.state.meta.acknowledged">
                            <i class="fas fa-check mr-1"></i>
                            {{ learner.name }}
                        </small>
                        -->

                    </template>
                </el-table-column>

                <el-table-column align="right">
                    <template v-slot="{row}">
                        <div class="p-1" v-if="!row.withdrawed">

                            <base-button type="primary" size="sm" class="ml-1"
                                    v-if="canReadRegistration(row)"
                                    @click="openRegistration(row)">
                                {{ $t('dashboard.more') }}
                            </base-button>

                            <base-button type="secondary" size="sm" class="mr-0 mb-1"
                                v-if="canReadOverview(row)"
                                @click="openOverview(row)"
                            >
                                {{ $t('dashboard.overview') }}
                            </base-button>

                        </div>
                    </template>
                </el-table-column>
                
                <el-table-column align="right" width="30" class-name="actions">
                    <template v-slot="{row}">
                        <div class="table-actions">
                            <actions-dropdown :actions="actions(row)" :large="true"></actions-dropdown>
                        </div>
                    </template>
                </el-table-column>

            </el-table>
        </div>

        <portal to="modals">
            <comment-modal v-model="modals.comment" :comment="comment">
            </comment-modal>
        </portal>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import CommentModal from './CommentModal'
    import moment from 'moment'

    export default {

        components: {
            CommentModal
        },

        props: {

            registrations: {
                type: Array,
                required: true
            },

            learner: {
                type: Object,
                required: true
            }
        },
        
        data() {
            return {
                modals: new ModalsController('comment'),
                comment: { 
                    title: null,
                    content: null,
                    author: null
                },
                moment: moment,
                order: 'asc',
            }
        },

        computed: {

            empty() {
                return this.registrations && this.registrations.length == 0
            },

            orderOptions() {
                return [
                    { value: 'asc', label: '<i class="fas fa-arrow-down"></i>' },
                    { value: 'desc', label: '<i class="fas fa-arrow-up"></i>' },
                ]
            },

            visibleRegistrations() {
                if (this.$permissions.hasRight('dashboard.learners.write')) {
                    return this.registrations
                }
                return this.registrations.filter(reg => !reg.withdrawed)
            }
        },

        watch: {
            order(value) {
                this.registrations.reverse()
            }
        },

        methods: {

            canWithdraw(registration) {
                return registration.explorable
                    && !registration.state.comment
                    && registration.state.completed_sessions == 0
                    && !registration.withdrawed
                    && this.$permissions.hasRight('dashboard.learners.write')
            },

            canBringback(registration) {
                return registration.explorable
                    && registration.withdrawed
                    && this.$permissions.hasRight('dashboard.learners.write')
            },

            canReadRegistration(registration) {
                return this.allowedStatus(registration) && registration.explorable
            },

            canReadOverview(registration) {
                return this.allowedStatus(registration)
            },

            allowedStatus(registration) {
                return (registration.status == 'active'
                    || (registration.status == 'archived' && this.$permissions.hasRight('dashboard.archives.read'))
                ) && !registration.withdrawed
            },

            actions(registration) {
                let actions = []
                if (this.canWithdraw(registration)) {
                    actions.push({
                        label: this.$t('dashboard.withdraw-from-learner-cursus'),
                        handler: this.withdraw,
                        data: registration
                    })
                }
                if (this.canBringback(registration)) {
                    actions.push({
                        label: this.$t('dashboard.bringback-to-learner-cursus'),
                        handler: this.bringback,
                        data: registration
                    })
                }
                return actions
            },

            withdraw(registration) {
                axios.post('/astral/api/front/cohorts/'+registration.cohort.id+'/members/'+this.learner.id+'/registrations/'+registration.id+'/withdraw')
                .then(resp => {
                    this.$emit('change');
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            bringback(registration) {
                axios.post('/astral/api/front/cohorts/'+registration.cohort.id+'/members/'+this.learner.id+'/registrations/'+registration.id+'/bringback')
                .then(resp => {
                    this.$emit('change');
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            progress(registration) {
                if (registration.state.sessions_count == 0) {
                    return 0
                }
                let progress = (registration.state.completed_sessions ? registration.state.completed_sessions : 0) * 100 / registration.state.sessions_count
                return parseInt(progress)
            },

            date(date) {
                return this.moment(date).format('YYYY/MM/DD')
            },
            
            openCohort(cohort) {
                this.$router.push({
                    name: 'dashboard-cohort',
                    params: {
                        cohortId: cohort.id,
                    }
                })
            },

            openRegistration(registration) {
                this.$router.push({
                    name: 'dashboard-learner-registration',
                    params: {
                        learnerId: this.$route.params.learnerId,
                        registrationId: registration.id
                    }
                })
            },

            openOverview(registration) {
                this.$router.push({
                    name: 'dashboard-learner-registration-overview',
                    params: {
                        learnerId: this.$route.params.learnerId,
                        registrationId: registration.id
                    }
                })
            }
        }
    }
</script>

<style scoped>

    .card >>> td {
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
        padding-right: 0;
        font-size: 0.8rem;
    }
    .card >>> td.actions {
        padding-left: 0rem;
        padding-right: 0.5rem;
    }
    .degrees >>> .progress {
        margin-bottom: 5px;
    }
    a.black-link {
        color: #525f7f;
    }
    a.black-link:hover {
        color: #5e72e4;
    }
    .padding {
        padding-top: 1rem;
        padding-bottom: 1rem;
    }
    .padding.disabled {
        padding-top: 0rem;
        padding-bottom: 0rem;
    }
    .disabled, .disabled h3, .disabled a {
        color: #bbb !important;
        margin-bottom: 0 !important;
        font-size: 0.8rem;
    }
    .badge.badge-success {
        background-color: #1aae6f;
        color: #fff;
        font-size: 0.65rem;
        text-transform: lowercase;
    }

</style>
